import { useMemo } from 'react';

import type { CourseContainerData, ModuleLesson } from '@yandex-int/k-common';
import groupBy from 'lodash/groupBy';

import { useModuleLessonsQuery, useModulesQuery } from '../../bundles/lab/components/library.course-container/queries';

function extendModuleLessonsByModuleId(lessons: ModuleLesson[], moduleId: number) {
    return lessons.map((lesson) => ({
        ...lesson,
        moduleId,
    }));
}

function getModuleLessonKey(module: ReturnType<typeof extendModuleLessonsByModuleId> extends (infer T)[] ? T : never) {
    return `${module.moduleId}_${module.group_name}`;
}

export const useLastLessonWidgetState = (
    courseContainerData: CourseContainerData | undefined,
    courseId: number | undefined
) => {
    const lastModuleId = courseContainerData?.module_lesson_link?.module?.id;
    const lastLessonId = courseContainerData?.module_lesson_link.lesson?.id;

    const { data: { cmodules: courseContainerCModules = [] } = {}, isLoading: isLoadingCModules } = useModulesQuery(
        courseContainerData?.container?.slug ?? ''
    );
    // определяем текущий и следующий модуль
    const [currentCModule, nextCModule] = courseContainerCModules.filter(
        (cModule, index, cModules) =>
            cModule.module.id === lastModuleId || (index !== 0 && cModules[index - 1].module.id === lastModuleId)
    );

    // получаем группировки уроков по каждому модулу
    const { data: currentModuleLessons, isLoading: isLoadingCurrentModuleLessons } = useModuleLessonsQuery(
        Number(currentCModule?.module?.id),
        courseId
    );
    const { data: nextModuleLessons, isLoading: isLoadingNextModuleLessons } = useModuleLessonsQuery(
        Number(nextCModule?.module?.id),
        courseId
    );

    const result: {
        /** имя группировки текущего урока */
        currentLessonGroupName?: string;
        /** имя группировки следующего урока */
        nextLessonGroupName?: string;
        /** уроки из текущей группировки */
        currentLessons?: ModuleLesson[];
        /** уроки из следующей группировки */
        nextLessons?: ModuleLesson[];
        /** id текущего модуля */
        currentModuleId?: number;
        /** id следующего модуля */
        nextModuleId?: number;
    } = useMemo(() => {
        // Расширяем уроки полем moduleId
        const extendedCurrentModuleLessons = extendModuleLessonsByModuleId(
            currentModuleLessons?.results ?? [],
            currentCModule?.module?.id
        );
        const extendedNextModuleLessons = extendModuleLessonsByModuleId(
            nextModuleLessons?.results ?? [],
            nextCModule?.module?.id
        );

        // Определяем группы уроков, которые содержат последний выданный урок и следующий за ним урок
        const allModuleLessonGroups = [...extendedCurrentModuleLessons, ...extendedNextModuleLessons];

        const currentLessonGroup = allModuleLessonGroups.find((lessonGroup) => lessonGroup.lesson.id === lastLessonId);
        const nextLessonGroup = allModuleLessonGroups.find((lessonGroup, index) => {
            if (index === 0) {
                return false;
            }

            // Предыдущая группа уроков содержит последний выданный урок
            const isPrevGroupContainsCurrentLesson =
                allModuleLessonGroups[index - 1].group_name === currentLessonGroup?.group_name;

            return (
                isPrevGroupContainsCurrentLesson &&
                // Эти группы уроков из разных модулей или эти группы уроков имеют разные названия
                (lessonGroup.moduleId !== currentLessonGroup.moduleId ||
                    lessonGroup.group_name !== currentLessonGroup?.group_name)
            );
        });

        const currentLessonGroupName = currentLessonGroup?.group_name ?? '';
        const nextLessonGroupName = nextLessonGroup?.group_name ?? '';

        // Помним, что в разных модулях могут быть группировки с одинаковым group_name
        const groupedLessonsGroup = groupBy(allModuleLessonGroups, getModuleLessonKey);

        return {
            currentLessons: groupedLessonsGroup[currentLessonGroup ? getModuleLessonKey(currentLessonGroup) : ''],
            nextLessons: groupedLessonsGroup[nextLessonGroup ? getModuleLessonKey(nextLessonGroup) : ''],
            currentLessonGroupName,
            nextLessonGroupName,
            currentModuleId: currentLessonGroup?.moduleId,
            nextModuleId: nextLessonGroup?.moduleId,
        };
    }, [currentModuleLessons, nextModuleLessons, lastLessonId, currentCModule, nextCModule]);

    return { ...result, isLoading: isLoadingCurrentModuleLessons || isLoadingNextModuleLessons || isLoadingCModules };
};
